import styled from '@emotion/styled'
import { map } from 'lodash'
import { ReactNode } from 'react'
import colors from '../colors'
import styles from '../styles'
import { smallTablet } from './responsive'

type Props<T> = {
	options: Array<{ key: T; label: ReactNode }>
	onChange: (value: T) => unknown
	value: T
	disabled?: boolean
}

const SELECTED_SHADOW = '0 2px 3px 0 rgba(0, 0, 0, 0.1)'

// A phone has no room for a row of labels: the options sit two to a row and
// the chosen one is marked in place. From a tablet up they share one row and
// the marker slides.
const Container = styled.div<{ disabled?: boolean }>`
	background-color: ${colors.inactiveGray};
	border-radius: 20px;
	position: relative;
	padding: 2px;
	opacity: ${props => (props.disabled ? 0.5 : 1)};

	${smallTablet} {
		height: 40px;
	}
`
const Options = styled.div`
	display: grid;
	font-size: 12px;
	grid-auto-rows: 36px;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	height: 100%;
	position: relative;
	width: 100%;

	${smallTablet} {
		display: flex;
	}
`
const Option = styled.div<{ disabled?: boolean; isSelected: boolean }>`
	align-items: center;
	background-color: ${props =>
		props.isSelected ? colors.white : 'transparent'};
	border-radius: 18px;
	box-shadow: ${props => (props.isSelected ? SELECTED_SHADOW : 'none')};
	cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
	display: flex;
	height: 100%;
	justify-content: center;
	min-width: 0;
	${styles.ellipsis};
	z-index: 1;

	${smallTablet} {
		background-color: transparent;
		box-shadow: none;
		flex-basis: 50%;
	}
`
const Switcher = styled.div`
	background-color: ${colors.white};
	border-radius: 18px;
	display: none;
	position: absolute;
	left: 0;
	height: 100%;
	width: 50%;
	box-shadow: ${SELECTED_SHADOW};
	transition: transform 0.3s ease-in-out;

	${smallTablet} {
		display: block;
	}
`
export function ToggleLarge<T>({
	options,
	onChange,
	value,
	disabled,
}: Props<T>): JSX.Element {
	const idx = options.findIndex(option => option.key === value)
	const optionSize = 100 / options.length
	return (
		<Container disabled={disabled}>
			<Options>
				<Switcher
					style={{
						transform: `translateX(${100 * idx}%)`,
						width: `${optionSize}%`,
					}}
				/>
				{map(options, option => (
					<Option
						key={'' + option.key}
						disabled={disabled}
						isSelected={option.key === value}
						onClick={() => !disabled && onChange(option.key)}
					>
						{option.label}
					</Option>
				))}
			</Options>
		</Container>
	)
}
